import { createUseStyles } from 'react-jss';

// Common color palette
export const colors = {
    primary: '#085d40',
    primaryHover: '#064532',
    primaryLight: '#047857',
    secondary: '#6b7280',
    secondaryHover: '#4b5563',
    background: '#ffffff',
    backgroundSecondary: '#f8fafc',
    backgroundTertiary: '#f9fafb',
    border: '#e2e8f0',
    borderLight: '#f3f4f6',
    text: '#1f2937',
    textSecondary: '#374151',
    textLight: '#6b7280',
    success: '#059669',
    successLight: '#ecfdf5',
    warning: '#f59e0b',
    warningLight: '#fef3c7',
    error: '#dc2626',
    errorLight: '#fef2f2',
    white: '#ffffff'
};

// Common gradient backgrounds
export const gradients = {
    primary: 'linear-gradient(135deg, #085d40 0%, #047857 100%)',
    primaryLight: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
    glass: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)'
};

// Common box shadows
export const shadows = {
    sm: '0 1px 3px rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px rgba(0, 0, 0, 0.07)',
    lg: '0 8px 8px rgba(0, 0, 0, 0.15)',
    xl: '0 4px 20px rgba(0, 0, 0, 0.08)',
    primary: '0 2px 8px rgba(8, 93, 64, 0.2)',
    primaryHover: '0 4px 12px rgba(8, 93, 64, 0.3)'
};

// Common styles hook
export const useCommonStyles = createUseStyles({
    // Container styles
    container: {
        backgroundColor: colors.background,
        minHeight: '100vh'
    },

    // Header styles
    headerSection: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        backgroundColor: colors.backgroundSecondary,
        borderRadius: '12px',
        padding: '20px 32px',
        boxShadow: shadows.lg
    },

    headerSectionGradient: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '-24px',
        marginBottom: '32px',
        background: gradients.primary,
        borderRadius: '12px',
        padding: '24px 32px',
        boxShadow: shadows.lg,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '150px',
            height: '150px',
            background: gradients.glass,
            borderRadius: '50%',
            transform: 'translate(50%, -50%)'
        }
    },

    // Typography styles
    pageTitle: {
        fontSize: '24px',
        fontWeight: 600,
        color: colors.text,
        margin: 0
    },

    pageTitleWhite: {
        fontSize: '28px',
        fontWeight: 700,
        color: colors.white,
        marginBottom: '8px'
    },

    pageSubtitle: {
        fontSize: '16px',
        color: '#d1d1d1ff',
        margin: '4px 0 0 0',
        fontWeight: 500
    },

    // Button styles
    primaryButton: {
        backgroundColor: colors.primary,
        color: colors.white,
        fontWeight: 600,
        padding: '10px 20px',
        borderRadius: '8px',
        textTransform: 'none',
        fontSize: '14px',
        boxShadow: shadows.primary,
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: colors.primaryHover,
            transform: 'translateY(-1px)',
            boxShadow: shadows.primaryHover
        }
    },

    secondaryButton: {
        borderColor: '#d1d5db',
        color: colors.textLight,
        fontWeight: 500,
        padding: '10px 20px',
        borderRadius: '8px',
        textTransform: 'none',
        minWidth: '100px',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
            borderColor: '#9ca3af',
            backgroundColor: colors.backgroundSecondary
        }
    },

    // Table styles
    tableContainer: {
        backgroundColor: colors.background,
        borderRadius: '12px',
        border: `1px solid ${colors.border}`,
        boxShadow: shadows.md,
        overflow: 'hidden'
    },

    tableHeader: {
        backgroundColor: colors.primary,
        borderBottom: `2px solid ${colors.primaryHover}`
    },

    tableHeaderCell: {
        fontWeight: 600,
        fontSize: '12px',
        color: colors.white,
        textTransform: 'uppercase',
        padding: '12px 16px'
    },

    tableCell: {
        padding: '12px 16px',
        fontSize: '13px',
        fontWeight: 500,
        color: colors.textSecondary,
        borderBottom: `1px solid ${colors.borderLight}`,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '150px'
    },

    tableRow: {
        backgroundColor: colors.background,
        '&:hover': {
            backgroundColor: colors.backgroundSecondary,
            transition: 'background-color 0.2s ease'
        },
        '&:nth-of-type(even)': {
            backgroundColor: colors.backgroundTertiary
        }
    },

    // Search styles
    searchField: {
        width: '300px',
        '& .MuiOutlinedInput-root': {
            backgroundColor: colors.background,
            borderRadius: '8px',
            '& fieldset': {
                borderColor: '#d1d5db'
            },
            '&:hover fieldset': {
                borderColor: colors.primary
            },
            '&.Mui-focused fieldset': {
                borderColor: colors.primary
            }
        },
        '& .MuiInputLabel-root': {
            color: colors.textLight
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: colors.primary
        }
    },

    searchButton: {
        cursor: 'pointer',
        backgroundColor: colors.primary,
        color: colors.white,
        padding: '10px',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        border: `1px solid ${colors.primary}`,
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: colors.primaryHover
        }
    },

    // Loading styles
    loadingContainer: {
        width: '100%',
        height: '400px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },

    // Error styles
    errorContainer: {
        marginTop: '16px',
        padding: '12px 16px',
        backgroundColor: colors.errorLight,
        border: '1px solid #fecaca',
        borderRadius: '8px',
        color: colors.error,
        fontSize: '14px',
        fontWeight: 500,
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },

    // Card styles
    card: {
        borderRadius: '16px',
        boxShadow: shadows.xl,
        border: `1px solid ${colors.border}`,
        backgroundColor: colors.background
    },

    cardContent: {
        padding: '32px'
    },

    // Status chip styles
    statusChip: {
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: 600,
        textTransform: 'capitalize'
    },

    statusActive: {
        backgroundColor: colors.successLight,
        color: colors.success,
        border: '1px solid #a7f3d0'
    },

    statusInactive: {
        backgroundColor: colors.errorLight,
        color: colors.error,
        border: '1px solid #fecaca'
    },

    statusPending: {
        backgroundColor: colors.warningLight,
        color: colors.warning,
        border: '1px solid #fcd34d'
    }
});

// Common dialog styles
export const dialogStyles = {
    paper: {
        borderRadius: '12px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
    },
    title: {
        fontSize: '18px',
        fontWeight: 600,
        color: colors.text,
        borderBottom: `1px solid ${colors.border}`,
        padding: '20px 24px 16px'
    },
    content: {
        padding: '20px 24px'
    },
    actions: {
        padding: '16px 24px',
        gap: 2
    }
};

// Common form field styles
export const formFieldStyles = {
    '& .MuiOutlinedInput-root': {
        backgroundColor: colors.backgroundSecondary,
        borderRadius: '8px',
        '&:hover': {
            backgroundColor: colors.borderLight
        },
        '&.Mui-focused': {
            backgroundColor: colors.background
        }
    },
    '& .MuiInputLabel-root': {
        color: colors.textLight,
        fontWeight: 500
    }
};
